import { FC } from "react";
import classNames from "classnames";
import { MultiValue, SingleValue } from "react-select";

import { useAppDispatch, useAppSelector } from "../../hooks/useRedux";
import { setSelectedModelId } from "../../store/overview/slice";
import { selectActiveModelsState, selectModelLoading } from "../../store/selectors/models";
import { selectSelectedModelIdState } from "../../store/selectors/overview";
import SelectCarma, { CustomOption } from "../Select";
import { ISelectCarmaOption } from "../Select/types";
import Skeleton from "../Skeleton/Skeleton";
import { DropdownIndicatorWithAiIcon } from "./Components/DropdownIndicatorWithAiIcon/DropdownIndicatorWithAiIcon";
import { OverviewModelSelectProps } from "./types";
import { ALL_MODELS_OPTION } from "./const";
import { isSingleSelectOption } from "../Select/utils/isSingleSelectOption";

import styles from "./OverviewModelSelect.module.css";



const OverviewModelSelect: FC<OverviewModelSelectProps> = ({ className, onChange }) => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectModelLoading);
  const models = useAppSelector(selectActiveModelsState);
  const selectedModelId = useAppSelector(selectSelectedModelIdState);

  const totalModelsCount = models?.length ?? 0;
  const allModelsOption: ISelectCarmaOption = {
    ...ALL_MODELS_OPTION,
    label: `AI Models: ${totalModelsCount}`,
  };

  const options: ISelectCarmaOption[] = !models?.length
    ? [allModelsOption]
    : [
        allModelsOption,
        ...models.map((model) => ({
          id: Number(model.id),
          label: `${model.name} ${model.data?.version || ""}`,
          value: Number(model.id),
        })),
      ];

  const selectedOption =
    selectedModelId == null ? allModelsOption : options.find((o) => o.id === selectedModelId) ?? allModelsOption;



  const handleChange = (newValue: SingleValue<ISelectCarmaOption> | MultiValue<ISelectCarmaOption>) => {
    if (!isSingleSelectOption(newValue)) return;

    const nextModelId = newValue.id === 0 ? null : newValue.id;
    if (selectedModelId === nextModelId) return;

    dispatch(setSelectedModelId(nextModelId));
    onChange?.(nextModelId);
  };



  if (isLoading || !models) {
    return (
      <Skeleton
        height={48}
        width="100%"
        className={classNames(styles["overview-model-select"], className)}
      />
    );
  }

  return (
    <div className={classNames(styles["overview-model-select"], className)}>
      <SelectCarma
        className={styles["overview-model-select__control"]}
        value={selectedOption}
        options={options}
        onChange={handleChange}
        components={{
          Option: CustomOption,
          DropdownIndicator: DropdownIndicatorWithAiIcon,
        }}
      />
    </div>
  );
};

export default OverviewModelSelect;

